import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import React, { useEffect, useMemo, useState } from 'react';

import Box from 'components/Box';
import { ErrorContainer, Label } from 'components/Form/Field';
import { useFormContext } from 'components/Form/FormContext';
import {
  StripeElementContainer,
  stripeElementStyles,
  useStripeFieldStatus,
  useStripeTokenFormInterceptor,
  withIdealistStripeProvider,
} from 'components/Form/Stripe';
import { AnimatedCheckmark } from 'components/Icon/Icon';
import Tooltip from 'components/Tooltip';
import { colors } from 'theme';

import { CreditCardCvcTooltip } from './CreditCardCvcTooltip';

type Props = {
  wrap?: boolean;
  disabled?: boolean;
  'data-qa-id'?: string;
};

function UnwrappedCreditCardField({
  wrap,
  disabled,
  'data-qa-id': dataQaId,
}: Props) {
  const [cardNumberStatus, onChangeCardNumber] = useStripeFieldStatus();
  const [cardExpiryStatus, onChangeCardExpiryStatus] = useStripeFieldStatus();
  const [cardCvcStatus, onChangeCardCvcStatus] = useStripeFieldStatus();
  const allComplete = useMemo(
    () =>
      cardNumberStatus.type === 'complete' &&
      cardExpiryStatus.type === 'complete' &&
      cardCvcStatus.type === 'complete',
    [cardNumberStatus, cardExpiryStatus, cardCvcStatus]
  );

  const [cardBrand, setCardBrand] = useState<string>();
  const { updateSubmitDisabled, fieldQaIdPrefix } = useFormContext();
  const qaId = dataQaId || fieldQaIdPrefix;
  const { stripeTokenError } = useStripeTokenFormInterceptor();

  useEffect(() => {
    updateSubmitDisabled(!allComplete);
  }, [cardExpiryStatus, allComplete, updateSubmitDisabled]);

  return (
    <Box m="-8px" mb={22}>
      <Box display="flex" flexWrap="wrap" data-qa-id={`${qaId}-credit-card`}>
        <Box position="relative" width={wrap ? 1 : [1, null, 1 / 2]} p="8px">
          <Label htmlFor={`${qaId}-number`}>{getText('Card Number')}</Label>

          <StripeElementContainer>
            <CardNumberElement
              id={`${qaId}-number`}
              options={{ showIcon: true, style: stripeElementStyles }}
              onChange={(event) => {
                setCardBrand(event.brand);
                onChangeCardNumber(event);
              }}
            />
          </StripeElementContainer>

          {cardNumberStatus.type === 'error' && (
            <ErrorContainer>{cardNumberStatus.errorMessage}</ErrorContainer>
          )}
        </Box>

        <Box
          position="relative"
          width={wrap ? [1, null, 1 / 2] : [1 / 2, null, 1 / 4]}
          p="8px"
        >
          <Label htmlFor={`${qaId}-expiry-date`}>
            {getText('Expiry Date')}
          </Label>

          <StripeElementContainer>
            <CardExpiryElement
              id={`${qaId}-expiry-date`}
              options={{ style: stripeElementStyles }}
              onChange={onChangeCardExpiryStatus}
            />
          </StripeElementContainer>

          {cardExpiryStatus.type === 'error' && (
            <ErrorContainer>{cardExpiryStatus.errorMessage}</ErrorContainer>
          )}
        </Box>

        <Box
          position="relative"
          width={wrap ? [1, null, 1 / 2] : [1 / 2, null, 1 / 4]}
          p="8px"
        >
          <Label htmlFor={`${qaId}-cvc`}>
            <Box display="flex" alignItems="center">
              {getText('CVC')}
              <Box ml="4px" verticalAlign="middle">
                <Tooltip disabled={disabled} tabIndex={-1}>
                  <Box textAlign="center">
                    <CreditCardCvcTooltip brand={cardBrand} />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Label>

          <StripeElementContainer>
            <CardCvcElement
              id={`${qaId}-cvc`}
              options={{ style: stripeElementStyles }}
              onChange={onChangeCardCvcStatus}
            />
          </StripeElementContainer>

          {cardCvcStatus.type === 'error' && (
            <ErrorContainer>{cardCvcStatus.errorMessage}</ErrorContainer>
          )}

          {allComplete && (
            <Box
              position="absolute"
              top={76}
              right={-22}
              transform="translateY(-50%)"
              display={['none', null, 'flex']}
              alignItems="center"
              justifyContent="center"
              width={20}
              height={20}
              borderRadius="50%"
              bg={colors.turtleGreen}
            >
              <AnimatedCheckmark
                width="10px"
                height="10px"
                stroke="white"
                strokeWidth="3px"
                delay={0}
              />
            </Box>
          )}
        </Box>
      </Box>

      {stripeTokenError && <ErrorContainer>{stripeTokenError}</ErrorContainer>}
    </Box>
  );
}

export const CreditCardField = withIdealistStripeProvider(
  UnwrappedCreditCardField
);
